import {
    Box,
    Button,
    Chip,
    FormControlLabel,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';

import InfoTooltip from 'components/v2/common/infoTooltip';
import { SectionSubtitle, SectionTitle } from '../../../components/analyticsUi';

import type { AssessmentAgentRanking, AssessmentAgentRankingItem } from 'hooks/analytics';
import useTranslate from 'hooks/useTranslate';
import formatDuration from '../../../utils/formatDuration';

interface AgentSpeedRankingProps {
    agentRanking: AssessmentAgentRanking;
    selectedAgentId?: number;
    onAgentSelect: (agentId?: number) => void;
}

interface RankingTableProps {
    agents: AssessmentAgentRankingItem[];
    selectedAgentId?: number;
    onAgentSelect: (agentId?: number) => void;
    showRank: boolean;
}

const RankingTable: FC<RankingTableProps> = ({
    agents,
    selectedAgentId,
    onAgentSelect,
    showRank,
}) => {
    const { translate } = useTranslate();

    return (
        <TableContainer>
            <Table size="small" sx={{ minWidth: 1040 }}>
                <TableHead>
                    <TableRow>
                        <TableCell>{translate('assessmentRank')}</TableCell>
                        <TableCell>{translate('assessmentAgent')}</TableCell>
                        <TableCell align="right">
                            {translate('assessmentCompletedAssessments')}
                        </TableCell>
                        <TableCell align="right">{translate('assessmentMedianHandling')}</TableCell>
                        <TableCell align="right">
                            {translate('assessmentAverageHandling')}
                        </TableCell>
                        <TableCell align="right">{translate('assessmentP90')}</TableCell>
                        <TableCell align="right">{translate('assessmentSpeedIndex')}</TableCell>
                        <TableCell align="right">
                            {translate('assessmentProviderComparison')}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {agents.map((agent) => {
                        const adjusted = agent.adjusted;
                        const comparison = adjusted?.percentFasterThanProvider;
                        const comparisonDirection =
                            comparison == null
                                ? null
                                : comparison >= 0
                                  ? translate('assessmentFaster')
                                  : translate('assessmentSlower');

                        return (
                            <TableRow
                                key={agent.agentId}
                                selected={agent.agentId === selectedAgentId}
                                hover
                            >
                                <TableCell sx={{ fontWeight: 800 }}>
                                    {showRank ? (agent.rank ?? '—') : '—'}
                                </TableCell>
                                <TableCell>
                                    <Button
                                        size="small"
                                        variant={
                                            agent.agentId === selectedAgentId ? 'contained' : 'text'
                                        }
                                        onClick={() =>
                                            onAgentSelect(
                                                agent.agentId === selectedAgentId
                                                    ? undefined
                                                    : agent.agentId
                                            )
                                        }
                                        sx={{
                                            justifyContent: 'flex-start',
                                            minWidth: 0,
                                            px: 1,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                        }}
                                    >
                                        {agent.agentName}
                                    </Button>
                                </TableCell>
                                <TableCell align="right">{agent.sampleSize}</TableCell>
                                <TableCell align="right">
                                    {agent.agentHandling.medianSeconds == null
                                        ? translate('assessmentNoDataValue')
                                        : formatDuration(agent.agentHandling.medianSeconds)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatDuration(agent.agentHandling.avgSeconds)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatDuration(agent.agentHandling.p90Seconds)}
                                </TableCell>
                                <TableCell align="right">
                                    {adjusted ? adjusted.speedIndex.toFixed(1) : '—'}
                                </TableCell>
                                <TableCell align="right">
                                    {comparison != null ? (
                                        <Chip
                                            size="small"
                                            label={`${Math.abs(comparison).toFixed(
                                                1
                                            )}% ${comparisonDirection}`}
                                            color={comparison >= 0 ? 'success' : 'warning'}
                                            variant="outlined"
                                        />
                                    ) : (
                                        '—'
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const AgentSpeedRanking: FC<AgentSpeedRankingProps> = ({
    agentRanking,
    selectedAgentId,
    onAgentSelect,
}) => {
    const { translate } = useTranslate();
    const [showZeroAssessmentAgents, setShowZeroAssessmentAgents] = useState(false);
    const rankedAgents = agentRanking.agents.filter((agent) => agent.eligibleForRanking);
    const insufficientAgents = agentRanking.agents.filter(
        (agent) =>
            !agent.eligibleForRanking &&
            (showZeroAssessmentAgents || agent.sampleSize > 0 || agent.agentId === selectedAgentId)
    );
    const rankedByLabel =
        agentRanking.rankedBy === 'adjustedSpeedIndex'
            ? translate('assessmentAdjustedSpeed')
            : translate('assessmentRawMedianHandling');

    return (
        <Paper component="section" variant="outlined" sx={{ borderRadius: 2, p: 2, mb: 2 }}>
            <SectionTitle>
                {translate('assessmentAgentSpeedRanking')}
                <InfoTooltip title={translate('assessmentHandlingTimeTooltip')} />
            </SectionTitle>
            <SectionSubtitle>
                {translate('assessmentRankedBy')} {rankedByLabel}.{' '}
                {translate('assessmentMinimumSample')} {agentRanking.minimumSampleSize}.
            </SectionSubtitle>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 1,
                }}
            >
                <FormControlLabel
                    control={
                        <Switch
                            checked={showZeroAssessmentAgents}
                            onChange={(event) => setShowZeroAssessmentAgents(event.target.checked)}
                            size="small"
                        />
                    }
                    label={translate('assessmentShowZeroAssessmentAgents')}
                    sx={{
                        m: 0,
                        '& .MuiFormControlLabel-label': {
                            color: 'text.secondary',
                            fontSize: 12,
                        },
                    }}
                />
                {selectedAgentId != null ? (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onAgentSelect(undefined)}
                        sx={{ textTransform: 'none' }}
                    >
                        {translate('assessmentShowAllAgents')}
                    </Button>
                ) : null}
            </Box>

            {rankedAgents.length > 0 ? (
                <RankingTable
                    agents={rankedAgents}
                    selectedAgentId={selectedAgentId}
                    onAgentSelect={onAgentSelect}
                    showRank
                />
            ) : (
                <Typography color="text.secondary" sx={{ py: 2, fontSize: 13 }}>
                    {translate('assessmentNoRankedAgents')}
                </Typography>
            )}

            {insufficientAgents.length > 0 ? (
                <Box sx={{ mt: 3 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 0.5 }}>
                        {translate('assessmentInsufficientData')}
                    </Typography>
                    <Typography color="text.secondary" sx={{ fontSize: 12, mb: 1 }}>
                        {translate('assessmentInsufficientRankingDescription')}
                    </Typography>
                    <RankingTable
                        agents={insufficientAgents}
                        selectedAgentId={selectedAgentId}
                        onAgentSelect={onAgentSelect}
                        showRank={false}
                    />
                </Box>
            ) : null}
        </Paper>
    );
};

export default AgentSpeedRanking;
